import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ProtectedRoute from '../components/ProtectedRoute/ProtectedRoute';
import PublicRoute from '../components/PublicRoute/PublicRoute';
import AuthInitializer from '../components/AuthInitializer/AuthInitializer';
import Layout from '../components/Layout';
import PageLoading from '../components/PageLoading/PageLoading';
import PermissionRoute from '../components/PermissionRoute';

// 懒加载页面组件
const LoginPage = lazy(() => import('../pages/Login/LoginPage'));
const ForgotPasswordPage = lazy(() => import('../pages/ForgotPassword/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPassword/ResetPasswordPage'));
const HomePage = lazy(() => import('../pages/Home/HomePage'));
const ChangePasswordPage = lazy(() => import('../pages/ChangePassword/ChangePasswordPage'));
const FunctionListPage = lazy(() => import('../pages/FunctionList/FunctionListPage'));
const RoleListPage = lazy(() => import('../pages/RoleList/RoleListPage'));
const EmployeeListPage = lazy(() => import('../pages/EmployeeList/EmployeeListPage'));
const OrganizationListPage = lazy(() => import('../pages/OrganizationList/OrganizationListPage'));
const GuardrailListPage = lazy(() => import('../pages/GuardrailList/GuardrailListPage'));
const ProjectListPage = lazy(() => import('../pages/ProjectList/ProjectListPage'));
const APIKeyListPage = lazy(() => import('../pages/APIKeyList/APIKeyListPage'));
const UserListPage = lazy(() => import('../pages/UserList/UserListPage'));
const ProviderListPage = lazy(() => import('../pages/ProviderList/ProviderListPage'));
const ModelListPage = lazy(() => import('../pages/ModelList/ModelListPage'));
const AccountListPage = lazy(() => import('../pages/AccountList/AccountListPage'));
const PriceCenterPage = lazy(() => import('../pages/PriceCenter/PriceCenterPage'));
const ReportPage = lazy(() => import('../pages/Report/ReportPage'));
const LogsPage = lazy(() => import('../pages/Logs/LogsPage'));
const TopupHistoryPage = lazy(() => import('../pages/TopupHistory/TopupHistoryPage'));
const PaymentOrdersPage = lazy(() => import('../pages/PaymentOrders/PaymentOrdersPage'));
const BlogStudioPage = lazy(() => import('../pages/BlogStudio/BlogStudioPage'));
const CareersStudioPage = lazy(() => import('../pages/CareersStudio/CareersStudioPage'));

/**
 * 应用路由配置
 * - /login: 登录页面（公开访问，已登录用户自动跳转）
 * - /home: 首页（需要登录）
 * - /: 根路径重定向到 /home
 */
export const router = createBrowserRouter([
  // 公开路由 - 已登录用户访问会自动跳转
  {
    element: <PublicRoute />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<PageLoading />}>
            <LoginPage />
          </Suspense>
        ),
      },
      {
        path: '/forgot-password',
        element: (
          <Suspense fallback={<PageLoading />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
      },
      {
        path: '/reset-password',
        element: (
          <Suspense fallback={<PageLoading />}>
            <ResetPasswordPage />
          </Suspense>
        ),
      },
    ],
  },

  // 受保护路由 - 需要登录才能访问，使用 Layout 布局
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: (
          <AuthInitializer>
            <Layout>
              <Suspense fallback={<PageLoading />}>
                <Outlet />
              </Suspense>
            </Layout>
          </AuthInitializer>
        ),
        children: [
          {
            path: '/home',
            element: <HomePage />,
          },
          // Overview routes - 需要权限控制
          {
            path: '/overview/token-report',
            element: (
              <PermissionRoute requiredFunction="token_report_view">
                <ReportPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/overview/logs',
            element: (
              <PermissionRoute requiredFunction="logs_view">
                <LogsPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/overview/topup-history',
            element: (
              <PermissionRoute requiredFunction="organization_list_view">
                <TopupHistoryPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/overview/payment-orders',
            element: (
              <PermissionRoute requiredFunction="organization_list_view">
                <PaymentOrdersPage />
              </PermissionRoute>
            ),
          },
          // 修改密码 - 不需要权限控制
          {
            path: '/change-password',
            element: <ChangePasswordPage />,
          },
          // 组织管理路由 - 需要权限控制
          {
            path: '/organizations/list',
            element: (
              <PermissionRoute requiredFunction="organization_list_view">
                <OrganizationListPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/organizations/guardrails',
            element: (
              <PermissionRoute requiredFunction="guardrail_list_view">
                <GuardrailListPage />
              </PermissionRoute>
            ),
          },
          // 项目管理路由 - 需要权限控制
          {
            path: '/projects/list',
            element: (
              <PermissionRoute requiredFunction="project_list_view">
                <ProjectListPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/projects/api-keys',
            element: (
              <PermissionRoute requiredFunction="api_key_list_view">
                <APIKeyListPage />
              </PermissionRoute>
            ),
          },
          // 用户管理路由 - 需要权限控制
          {
            path: '/users/list',
            element: (
              <PermissionRoute requiredFunction="user_list_view">
                <UserListPage />
              </PermissionRoute>
            ),
          },
          // 模型管理路由 - 需要权限控制
          {
            path: '/models/providers',
            element: (
              <PermissionRoute requiredFunction="provider_list_view">
                <ProviderListPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/models/list',
            element: (
              <PermissionRoute requiredFunction="model_list_view">
                <ModelListPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/models/accounts',
            element: (
              <PermissionRoute requiredFunction="account_list_view">
                <AccountListPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/models/price-center',
            element: (
              <PermissionRoute requiredFunction="price_center_view">
                <PriceCenterPage />
              </PermissionRoute>
            ),
          },
          // Content routes - 内容管理
          {
            path: '/content/blog-studio',
            element: <BlogStudioPage />,
          },
          {
            path: '/content/careers-studio',
            element: <CareersStudioPage />,
          },
          // 管理设置路由 - 需要权限控制
          {
            path: '/admin/functions',
            element: (
              <PermissionRoute requiredFunction="function_list_view">
                <FunctionListPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/admin/roles',
            element: (
              <PermissionRoute requiredFunction="role_list_view">
                <RoleListPage />
              </PermissionRoute>
            ),
          },
          {
            path: '/admin/employees',
            element: (
              <PermissionRoute requiredFunction="employee_list_view">
                <EmployeeListPage />
              </PermissionRoute>
            ),
          },
          // 可以在这里添加更多受保护的路由
          // {
          //   path: '/users',
          //   element: <UsersPage />,
          // },
        ],
      },
    ],
  },

  // 根路径重定向
  {
    path: '/',
    element: <Navigate to="/home" replace />,
  },

  // 404 页面 - 未匹配的路由重定向到 home
  {
    path: '*',
    element: <Navigate to="/home" replace />,
  },
]);

export default router;
